/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import { I18n } from '@coze-arch/i18n';
import { IconCozArrowLeft } from '@coze-arch/coze-design/icons';

import styles from './index.module.less';

export type PublishStep = 'form' | 'publishing' | 'success';

const STEP_ITEM_KEYS = [
  {
    labelKey: 'abr_app_step_confirm',
    hintKey: 'abr_app_step_confirm_hint',
  },
  {
    labelKey: 'abr_app_step_publish',
    hintKey: 'abr_app_step_publish_hint',
  },
  {
    labelKey: 'abr_app_step_done',
    hintKey: 'abr_app_step_done_hint',
  },
] as const;

export const stepToIndex = (step: PublishStep): number => {
  if (step === 'success') {
    return 2;
  }
  if (step === 'publishing') {
    return 1;
  }
  return 0;
};

export interface PublishStepsHeaderProps {
  stepIndex: number;
  onBack: () => void;
}

export const PublishStepsHeader: FC<PublishStepsHeaderProps> = ({
  stepIndex,
  onBack,
}) => (
  <header className={styles.header}>
    <div className={styles.headerRow}>
      <button type="button" className={styles.backBtn} onClick={onBack}>
        <IconCozArrowLeft />
      </button>
      <div>
        <div className={styles.breadcrumb}>
          {I18n.t('abr_app_publish_breadcrumb')}
        </div>
        <div className={styles.title}>{I18n.t('abr_app_publish_title')}</div>
      </div>
    </div>
    <div className={styles.steps}>
      {STEP_ITEM_KEYS.map((item, index) => {
        const isLast = index === 2;
        const done = index < stepIndex;
        const active = index === stepIndex;
        return (
          <div
            key={item.labelKey}
            className={`${styles.step} ${isLast ? styles.stepLast : ''} ${done ? styles.stepDone : ''} ${active ? styles.stepActive : ''}`}
          >
            <div className={styles.stepDot}>{done ? '✓' : index + 1}</div>
            <div className={styles.stepText}>
              <div className={styles.stepLabel}>{I18n.t(item.labelKey)}</div>
              <div className={styles.stepHint}>{I18n.t(item.hintKey)}</div>
            </div>
            {!isLast ? <div className={styles.stepLine} /> : null}
          </div>
        );
      })}
    </div>
  </header>
);
